import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import { usersApi, type UserRow } from '@/services/auth';

import styles from './ConfigUsuarios.module.css';

export function ConfigUsuarios() {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  const qc = useQueryClient();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [msg, setMsg] = useState<string | null>(null);

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: () => usersApi.create(nome.trim(), email.trim().toLowerCase(), role),
    onSuccess: (u) => {
      setMsg(`Criado: ${u.email} (senha temporária NcN3ws — troca no 1º acesso)`);
      setNome(''); setEmail(''); setRole('editor');
      invalidate();
    },
    onError: (e) =>
      setMsg(e instanceof ApiError && e.status === 409 ? 'Esse e-mail já existe.' : 'Erro ao criar.'),
  });

  const toggleActive = useMutation({
    mutationFn: (u: UserRow) => usersApi.patch(u.id, { is_active: !u.is_active }),
    onSuccess: invalidate,
  });

  const reset = useMutation({
    mutationFn: (u: UserRow) => usersApi.resetPassword(u.id),
    onSuccess: (r) => { setMsg(`Senha reposta para "${r.temp_password}" (troca no próximo acesso).`); invalidate(); },
  });

  return (
    <section className={styles.root} aria-label={t('config:sections.usersOnly', 'Usuários')}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Usuários</h1>
          <p className={styles.subtitle}>
            Equipe com acesso ao sistema. Login pelo e-mail; novos usuários
            recebem senha temporária e trocam no primeiro acesso.
          </p>
        </div>
        <span className={styles.count}>
          {isLoading ? '…' : `${users?.length ?? 0} usuários`}
        </span>
      </header>

      {msg && <p className={styles.flash} onClick={() => setMsg(null)}>{msg}</p>}

      <form
        className={styles.addBar}
        onSubmit={(e) => { e.preventDefault(); if (nome && email) create.mutate(); }}
      >
        <input className={styles.input} placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
        <input className={styles.input} placeholder="e-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className={styles.select} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="editor">Editor</option>
          <option value="admin">Admin</option>
        </select>
        <Button type="submit" disabled={create.isPending || !nome || !email}>
          Adicionar
        </Button>
      </form>

      <table className={styles.table}>
        <thead>
          <tr><th>Nome</th><th>E-mail</th><th>Papel</th><th>Estado</th><th></th></tr>
        </thead>
        <tbody>
          {(users ?? []).map((u) => (
            <tr key={u.id} className={u.is_active ? '' : styles.off}>
              <td className={styles.nome}>{u.nome}</td>
              <td className={styles.email}>{u.email}</td>
              <td>
                <Badge tone={u.role === 'admin' ? 'warn' : 'neutral'} size="xs">{u.role}</Badge>
              </td>
              <td>
                {u.is_active
                  ? <Badge tone="ok" size="xs">{u.must_change_password ? 'pendente 1º acesso' : 'ativo'}</Badge>
                  : <Badge tone="neutral" size="xs">inativo</Badge>}
              </td>
              <td className={styles.actions}>
                <button className={styles.link} onClick={() => reset.mutate(u)}>repor senha</button>
                <button className={styles.link} onClick={() => toggleActive.mutate(u)}>
                  {u.is_active ? 'desativar' : 'reativar'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
